export enum ClerkUserRole {
  SUPER_ADMIN = 'org:super_admin',
  ENGEN_ADMIN = 'org:engen_admin',
  CLIENT_ADMIN = 'org:client_admin',
  LEARNER = 'org:learner',
  // WIP: The following roles are still work in progress and might change significantly in the future.
  INSTRUCTOR = 'org:instructor',
  COACH = 'org:coach',
  CURRICULUM_DESIGNER = 'org:curriculum_designer',
}

// Role enum without org: prefix for API communication
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ENGEN_ADMIN = 'engen_admin',
  CLIENT_ADMIN = 'client_admin',
  LEARNER = 'learner',
  INSTRUCTOR = 'instructor',
  COACH = 'coach',
  CURRICULUM_DESIGNER = 'curriculum_designer',
}

export enum RoutePattern {
  DASHBOARD = '/admin',
  ORGANIZATIONS = '/admin/organizations/**',
  SYSTEM_SETTINGS = '/admin/system-settings/**',
  PA_SCORING_CONFIGS = '/admin/pa-scoring-configs/**',
  TRIAL_SIMULATOR = '/trial-intake-simulator',
  AUTOMATED_COMMS_SIMULATOR = '/automated-comms-simulator',
  TRIALS = '/admin/trials/**',
  USER_MANAGEMENT = '/admin/users/**',
  COACHING = '/admin/coaching/**',
  COACHING_MEETINGS = '/admin/coaching/meetings/**',
  LEARNER_COACHING = '/coaching/**',
  MY_AVAILABILITY = '/admin/my-availability/**',
  MY_LEARNERS = '/admin/my-learners/**',
  MY_MESSAGES = '/admin/my-messages/**',
  MY_COACH_PROFILE = '/admin/my-profile/**',
  COACH_CONTENT = '/admin/content/**',
  ADMIN_PERFORMANCE_HISTORY = '/admin/performance-history/**',
  ADMIN_LEARNER_ASSESSMENTS = '/admin/learner-assessments/**',
  ADMIN_ASSESSMENTS = '/admin/assessment/**',
  MY_ORGANIZATION = '/admin/my-organization/**',
  MY_COURSES = '/admin/my-courses/**',
  REPORTING = '/admin/reporting/**',
  DASHBOARD_GROUP_CLASSES = '/admin/group-classes/**',
  DASHBOARD_PRIVATE_CLASSES = '/admin/private-classes/**',
  GROUP_CLASSES = '/group-classes/**',
  PRIVATE_CLASSES = '/private-classes/**',
  WRITING_HELPER_ADMIN = '/admin/writing-helper/**',
  ADMIN_DASHBOARD_COURSES = '/admin/courses/**',
  COURSES = '/courses/**',
  ADMIN_LESSONS = '/admin/lessons/**',
  ADMIN_HEADWORDS = '/admin/headwords/**',
  LESSONS = '/lessons/**',
  ADMIN_ASSIGNMENTS = '/admin/assignments/**',
  CATALOG = '/catalog/**',
  GRAMMAR = '/grammar/**',
  INSTRUCTOR = '/instructor/**',
}

/** A route rule that the role alone cannot answer. */
export enum RouteCapability {
  MANAGE_COACHING = 'manage_coaching',
}

export type RouteAccess = {
  readonly routes: readonly RoutePattern[]
  readonly allowedRoles: readonly ClerkUserRole[]
  /** Also admits a caller with this capability, whatever their role. */
  readonly capability?: RouteCapability
}

export const ROUTE_ACCESS_CONFIG: readonly RouteAccess[] = [
  {
    // A coach's own meeting room (ENG-3360). Coach only, and ahead of COACHING: the first match wins.
    routes: [RoutePattern.COACHING_MEETINGS],
    allowedRoles: [ClerkUserRole.COACH],
  },
  {
    // The coaching portal manager tools. No role here: useCanManageCoaching holds who gets them.
    routes: [RoutePattern.COACHING],
    allowedRoles: [],
    capability: RouteCapability.MANAGE_COACHING,
  },
  {
    // A learner's own coaching conversation (ENG-3375). Learners, plus the two
    // internal admin roles — not so they can see a learner's page, which they
    // cannot: every surface here resolves from the signed-in user, so an admin
    // opening it asks who *their* coach is and is told one is being assigned.
    // They are allowed through only so the route is reachable while building and
    // supporting it, and a coach's view of a learner's conversation is
    // /admin/my-messages. Coaches are excluded for the same reason it would be
    // useless to them.
    routes: [RoutePattern.LEARNER_COACHING],
    allowedRoles: [ClerkUserRole.LEARNER, ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN],
  },
  {
    // A coach's own availability (ENG-3564). It resolves from the signed-in user, so
    // a super admin who opens it gets OwnCoachWorkspaceGuard's notice. Another
    // coach's week is a coaching manager's, from Staffing (ENG-4088). `engen_admin`
    // stays out: the role says nothing about whether the caller has a coaching day.
    routes: [RoutePattern.MY_AVAILABILITY],
    allowedRoles: [ClerkUserRole.COACH, ClerkUserRole.SUPER_ADMIN],
  },
  {
    // The coaching conversation (ENG-3374). The same two roles as MY_AVAILABILITY
    // above and for the same reasons: this page is the signed-in coach's own
    // worklist, derived from their own id, so a super admin opening it asks for a
    // caseload they do not have and gets an empty screen.
    //
    // Still narrower than the API, which admits every coaching oversight role.
    // That is not a contradiction: an EnGen admin may read any thread, and the
    // door for it is the learner's own record, not a page called "my messages".
    routes: [RoutePattern.MY_MESSAGES],
    allowedRoles: [ClerkUserRole.COACH, ClerkUserRole.SUPER_ADMIN],
  },
  {
    // The signed-in coach's caseload (ENG-3587, S20). The same two roles as the
    // other personal coach surfaces and for the same reasons: the page resolves
    // from the signed-in user, so a super admin opening it asks for a caseload
    // they do not have. They are admitted so the surface is reachable for
    // support; the navigation never offers it, and OwnCoachWorkspaceGuard is
    // what they see if they type the URL.
    //
    // The `/**` also covers S21 (`/admin/my-learners/:learnerUserId`), which is
    // the row target for this list and for the cross-coach view (A2).
    routes: [RoutePattern.MY_LEARNERS],
    allowedRoles: [ClerkUserRole.COACH, ClerkUserRole.SUPER_ADMIN],
  },
  {
    // A coach's own learner-facing profile. The same two roles as the two above:
    // it is written from the signed-in user, and an admin filling one in for a
    // coach does it from the Coaching list.
    routes: [RoutePattern.MY_COACH_PROFILE],
    allowedRoles: [ClerkUserRole.COACH, ClerkUserRole.SUPER_ADMIN],
  },
  {
    // Content assignments in the coach portal (ENG-3866). Admins use /admin/assignments.
    routes: [RoutePattern.COACH_CONTENT],
    allowedRoles: [ClerkUserRole.COACH, ClerkUserRole.SUPER_ADMIN],
  },
  {
    routes: [RoutePattern.ORGANIZATIONS],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN],
  },
  {
    // Super Admin only, and split out from ORGANIZATIONS above to say so. The
    // navigation already treats it this way — `useRoleBasedNavigation` adds
    // SYSTEM_SETTINGS for SUPER_ADMIN and not for ENGEN_ADMIN — so grouping it
    // with ORGANIZATIONS left a page that never appears in an EnGen Admin's
    // sidebar but still opens if they type the URL. The page issues access
    // tokens and lists cross-organization recipient addresses, so the narrower
    // of the two intents is the right one to keep.
    routes: [RoutePattern.SYSTEM_SETTINGS],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN],
  },
  {
    routes: [RoutePattern.PA_SCORING_CONFIGS],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN],
  },
  {
    routes: [RoutePattern.TRIAL_SIMULATOR, RoutePattern.AUTOMATED_COMMS_SIMULATOR],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN],
  },
  {
    routes: [RoutePattern.TRIALS],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN],
  },
  {
    routes: [RoutePattern.ADMIN_PERFORMANCE_HISTORY],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN, ClerkUserRole.CLIENT_ADMIN],
  },
  {
    routes: [RoutePattern.INSTRUCTOR],
    allowedRoles: [ClerkUserRole.INSTRUCTOR, ClerkUserRole.SUPER_ADMIN],
  },
  {
    routes: [RoutePattern.ADMIN_LEARNER_ASSESSMENTS],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.CLIENT_ADMIN, ClerkUserRole.INSTRUCTOR],
  },
  {
    routes: [RoutePattern.ADMIN_ASSESSMENTS],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN],
  },
  {
    routes: [RoutePattern.USER_MANAGEMENT],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN, ClerkUserRole.CLIENT_ADMIN],
  },
  {
    routes: [RoutePattern.MY_ORGANIZATION],
    allowedRoles: [ClerkUserRole.CLIENT_ADMIN],
  },
  {
    routes: [RoutePattern.MY_COURSES],
    // TODO: [ENG-488] Temporary Coach permission. Re-evaluate when coach requirements are finalized.
    allowedRoles: [ClerkUserRole.LEARNER, ClerkUserRole.COACH],
  },
  {
    // A coach lands here too (ENG-3374). They could not reach /admin at all
    // before, so signing in as one produced an access denial with no way
    // forward. What renders is the coach workspace, not the administration
    // dashboard — `Dashboard.tsx` branches on the role.
    routes: [RoutePattern.DASHBOARD],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.COACH,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
  {
    routes: [RoutePattern.DASHBOARD_GROUP_CLASSES],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN, ClerkUserRole.INSTRUCTOR],
  },
  {
    routes: [RoutePattern.DASHBOARD_PRIVATE_CLASSES],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.INSTRUCTOR],
  },
  {
    routes: [RoutePattern.GROUP_CLASSES],
    // TODO: [ENG-488] Temporary permission for Coach. Re-evaluate when coach requirements are finalized.
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.LEARNER,
      ClerkUserRole.COACH,
    ],
  },
  {
    routes: [RoutePattern.PRIVATE_CLASSES],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.LEARNER,
      ClerkUserRole.COACH,
    ],
  },
  {
    routes: [RoutePattern.REPORTING],
    allowedRoles: [ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN, ClerkUserRole.CLIENT_ADMIN],
  },
  {
    routes: [RoutePattern.WRITING_HELPER_ADMIN],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.INSTRUCTOR,
    ],
  },
  {
    routes: [RoutePattern.ADMIN_DASHBOARD_COURSES, RoutePattern.ADMIN_LESSONS, RoutePattern.ADMIN_ASSIGNMENTS],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
  {
    routes: [RoutePattern.ADMIN_HEADWORDS],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
  {
    routes: [RoutePattern.LESSONS],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.LEARNER,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.COACH,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
  {
    routes: [RoutePattern.COURSES],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.LEARNER,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.COACH,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
  {
    routes: [RoutePattern.CATALOG],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.LEARNER,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.COACH,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
  {
    routes: [RoutePattern.GRAMMAR],
    allowedRoles: [
      ClerkUserRole.SUPER_ADMIN,
      ClerkUserRole.ENGEN_ADMIN,
      ClerkUserRole.CLIENT_ADMIN,
      ClerkUserRole.LEARNER,
      ClerkUserRole.INSTRUCTOR,
      ClerkUserRole.COACH,
      ClerkUserRole.CURRICULUM_DESIGNER,
    ],
  },
] as const
